"use client";

import BellIcon from "~/assets/icons/bell.svg";
import PinIcon from "~/assets/icons/pin.svg";
import NewsCard from "~/components/ui/NewsCard";
import {
  QueryClientProvider,
  useInfiniteQuery,
  useQuery, useQueryClient,
} from "@tanstack/react-query";
import { getNews } from "~/app/api/_backend/news";
import { PiSmileySadLight } from "react-icons/pi";
import { Progress } from "~/components/ui/progress";
import React from "react";
import { FetchLoading } from "~/components/ui/FetchLoading";
import getQueryClient from "~/lib/getQueryClient";

export default function NewsPage() {
  const queryClient = getQueryClient();

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <NewsPinned />
      </QueryClientProvider>

      <section className="m-auto mt-36 w-11/12 sm:w-10/12 xl:w-6/12">
        <h2 className="font-bold !text-primary">
          <span className="mr-4 inline-block align-middle">
            <BellIcon />
          </span>
          Novinky
        </h2>
        <QueryClientProvider client={queryClient}>
          <News />
        </QueryClientProvider>
      </section>
    </>
  );
}

const NewsPinned = () => {
  const { isPending, isError, data } = useQuery({
    queryKey: ["news_pinned"],
    queryFn: async () => {
      return await getNews({ pinned: true }, { sort: ["createdAt,desc"] });
    },
  });

  if (isPending || isError) {
    return <></>;
  }

  if (data?.content.length <= 0) {
    return <></>;
  }

  return (
    <>
      <section className="m-auto mt-36 w-11/12 sm:w-10/12 xl:w-6/12">
        <h2 className="font-bold !text-primary">
          <span className="mr-4 inline-block align-middle">
            <PinIcon />
          </span>
          Přišpendlené
        </h2>

        {data?.content.map((news) => {
          return (
            <NewsCard header={news.title} date={new Date(news.createdAt)} key={news.title}>
              {news.content.replaceAll("\\n", "\n")}
            </NewsCard>
          );
        })}
      </section>
    </>
  );
};

const News = () => {
  const queryClient = useQueryClient();
  const {
    isPending,
    isError,
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["news"],
    queryFn: async ({ pageParam }) => {
      return await getNews(
        { pinned: false },
        { page: pageParam, size: 5, sort: ["createdAt,desc"] },
      );
    },
    initialPageParam: 0,
    getNextPageParam: ({ currentPage, totalPages }) => {
      const nextPage = currentPage + 1;
      if (nextPage > totalPages) return null;
      return nextPage;
    },
  });

  if (isPending) {
    return <FetchLoading />;
  }

  if (isError) {
    return (
      <div className="mt-5 flex flex-col text-primary">
        <div className="mb-3 text-3xl">
          <PiSmileySadLight />
        </div>
        <div>
          Nepodařilo se načíst novinky.{" "}
          <span
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["news"] })
            }
            className="underline transition-all hover:cursor-pointer hover:no-underline hover:brightness-75"
          >
            Načíst znova
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      {data?.pages.map((news, i) => (
        <React.Fragment key={i}>
          {news.content.map((news) => (
            <NewsCard
              header={news.title}
              date={new Date(news.createdAt)}
              key={news.title}
            >
              {news.content}
            </NewsCard>
          ))}
        </React.Fragment>
      ))}

      <div className="mt-24 flex flex-col items-center gap-4">
        <p className="text-primary">
          Zobrazeno {data?.pages.reduce((acc, c) => acc + c.content.length, 0)}{" "}
          z {data?.pages?.[0]?.totalElements}
        </p>
        <div className={"w-9/12 sm:w-6/12"}>
          <Progress
            value={
              (data?.pages.reduce((acc, c) => acc + c.content.length, 0) /
                (data?.pages?.[0]?.totalElements ?? 1)) *
              100
            }
          />
        </div>
        <button
          disabled={!hasNextPage || isFetching || isFetchingNextPage}
          onClick={() => fetchNextPage()}
          className="enabled:ripple btn btn-primary"
        >
          {isFetchingNextPage ? (
            <span>Načítávám</span>
          ) : (
            <span>Načíst další</span>
          )}
        </button>
      </div>
    </>
  );
};
